import type {
  AdminClaimRequest,
  AdminUser,
  ClaimRequest,
  ProposedDevice,
  CommandRecord,
  CommandType,
  Device,
  DeviceEvent,
  TrackPoint,
  Trip,
  TripSummary,
  User,
} from './types';

const ACCESS_KEY = 'gps.accessToken';
const REFRESH_KEY = 'gps.refreshToken';

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/** Server-sent error, carrying the status so callers can branch on it. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshing: Promise<boolean> | null = null;

/**
 * Swap the refresh token for a new access token.
 * Concurrent 401s share one in-flight refresh so a page full of widgets does
 * not fire five refreshes and invalidate each other.
 */
async function refreshAccessToken(): Promise<boolean> {
  if (refreshing) return refreshing;

  refreshing = (async () => {
    const refreshToken = tokens.refresh;
    if (!refreshToken) return false;

    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) {
      tokens.clear();
      return false;
    }

    const data = await response.json();
    tokens.set(data.accessToken, data.refreshToken);
    return true;
  })().finally(() => {
    refreshing = null;
  });

  return refreshing;
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; retry?: boolean } = {},
): Promise<T> {
  const { method = 'GET', body, retry = true } = options;
  const access = tokens.access;

  // Only declare a JSON body when there actually is one: Fastify rejects a
  // request that announces application/json and then sends nothing, which is
  // exactly what a bodyless POST (release, re-issue code) or DELETE looks like.
  const response = await fetch(path, {
    method,
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(access ? { authorization: `Bearer ${access}` } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

  if (response.status === 401 && retry && tokens.refresh) {
    if (await refreshAccessToken()) {
      return request<T>(path, { ...options, retry: false });
    }
  }

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;

  if (!response.ok) {
    throw new ApiError(response.status, data?.error ?? response.statusText, data?.code);
  }
  return data as T;
}

export const api = {
  async login(email: string, password: string) {
    const data = await request<{ user: User; accessToken: string; refreshToken: string }>(
      '/api/auth/login',
      { method: 'POST', body: { email, password }, retry: false },
    );
    tokens.set(data.accessToken, data.refreshToken);
    return data.user;
  },

  /** Self-service sign-up. The account can see nothing until a device is claimed. */
  async register(body: { email: string; name: string; password: string; phone?: string }) {
    const data = await request<{ user: User; accessToken: string; refreshToken: string }>(
      '/api/auth/register',
      { method: 'POST', body, retry: false },
    );
    tokens.set(data.accessToken, data.refreshToken);
    return data.user;
  },

  logout() {
    tokens.clear();
  },

  /** Ask for a vehicle. Grants nothing until an administrator approves it. */
  requestClaim: (
    body: ProposedDevice & { imei: string; note?: string },
  ) =>
    request<{ id: number; deviceKnown: boolean }>('/api/devices/claim-requests', {
      method: 'POST',
      body,
    }),

  claimRequests: () =>
    request<{ requests: ClaimRequest[] }>('/api/devices/claim-requests').then((r) => r.requests),

  me: () => request<{ user: User }>('/api/auth/me').then((r) => r.user),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>('/api/auth/password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),

  devices: () => request<{ devices: Device[] }>('/api/devices').then((r) => r.devices),

  /** Public: used by the login screen for the support address. */
  health: () =>
    request<{
      ok: boolean;
      supportEmail: string;
      retentionDays: number;
      devicesOnline: number;
    }>('/api/health', { retry: false }),

  /** Whether this account wants alerts about this vehicle. Per grant. */
  setNotify: (deviceId: number, notify: boolean) =>
    request<{ notify: boolean }>(`/api/devices/${deviceId}/notify`, {
      method: 'PUT',
      body: { notify },
    }),

  /**
   * Mirror this vehicle's raw traffic to a second server. `null` turns it off.
   *
   * Takes effect on the tracker's next connection: the mirror copies a whole
   * session from its login onward and cannot join one in progress.
   */
  setForward: (deviceId: number, target: { host: string; port: number } | null) =>
    request<{ forwarding: { host: string; port: number } | null }>(
      `/api/devices/${deviceId}/forward`,
      { method: 'PUT', body: target ?? { host: null, port: null } },
    ),

  /**
   * The whole export, as a download. Streamed NDJSON — never held in memory
   * here or on the server, because a hundred vehicles is millions of rows.
   */
  backupUrl: (from?: Date, to?: Date) => {
    const params = new URLSearchParams();
    if (from) params.set('from', from.toISOString());
    if (to) params.set('to', to.toISOString());
    const query = params.toString();
    return `/api/admin/backup${query ? `?${query}` : ''}`;
  },

  track: (deviceId: number, from: Date, to: Date, maxPoints = 6000) =>
    request<{ count: number; totalCount: number; points: TrackPoint[] }>(
      `/api/devices/${deviceId}/track?from=${from.toISOString()}&to=${to.toISOString()}` +
        `&maxPoints=${maxPoints}`,
    ),

  trips: (deviceId: number, from: Date, to: Date, includePoints = false, maxPoints = 6000) =>
    request<{ summary: TripSummary; trips: Trip[]; totalCount: number }>(
      `/api/devices/${deviceId}/trips?from=${from.toISOString()}&to=${to.toISOString()}` +
        `&includePoints=${includePoints}&maxPoints=${maxPoints}`,
    ),

  events: (deviceId: number, limit = 50) =>
    request<{ events: DeviceEvent[] }>(`/api/devices/${deviceId}/events?limit=${limit}`).then(
      (r) => r.events,
    ),

  sendCommand: (deviceId: number, type: CommandType, params: Record<string, string | number> = {}) =>
    request<{ id: number; status: 'sent' | 'pending'; queued: boolean }>(
      `/api/devices/${deviceId}/commands`,
      { method: 'POST', body: { type, params } },
    ),

  commands: (deviceId: number) =>
    request<{ commands: CommandRecord[] }>(`/api/devices/${deviceId}/commands`).then(
      (r) => r.commands,
    ),

  admin: {
    users: () => request<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users),

    createUser: (body: {
      email: string;
      name: string;
      password: string;
      phone?: string;
      role?: 'admin' | 'user';
    }) => request<{ id: number }>('/api/admin/users', { method: 'POST', body }),

    updateUser: (
      id: number,
      body: {
        name?: string;
        email?: string;
        phone?: string | null;
        role?: 'admin' | 'user';
        disabled?: boolean;
        password?: string;
      },
    ) =>
      request<{ ok: true; passwordChanged: boolean }>(`/api/admin/users/${id}`, {
        method: 'PATCH',
        body,
      }),

    deleteUser: (id: number) =>
      request<{ ok: true }>(`/api/admin/users/${id}`, { method: 'DELETE' }),

    /** Grants a user already has. The editor must start from these. */
    userDevices: (id: number) =>
      request<{ devices: Array<{ deviceId: number; level: 'owner' | 'viewer'; name: string }> }>(
        `/api/admin/users/${id}/devices`,
      ).then((r) => r.devices),

    setUserDevices: (id: number, devices: Array<{ deviceId: number; level: 'owner' | 'viewer' }>) =>
      request<{ ok: true }>(`/api/admin/users/${id}/devices`, { method: 'PUT', body: { devices } }),

    createDevice: (body: {
      imei: string;
      name: string;
      model: string;
      plate?: string;
      simNumber?: string;
      hasImmobiliser?: boolean;
    }) => request<{ id: number; protocol: string }>('/api/admin/devices', { method: 'POST', body }),

    updateDevice: (id: number, body: Record<string, unknown>) =>
      request<{ ok: true }>(`/api/admin/devices/${id}`, { method: 'PATCH', body }),

    deleteDevice: (id: number) =>
      request<{ ok: true }>(`/api/admin/devices/${id}`, { method: 'DELETE' }),

    claimRequests: (status?: 'pending' | 'approved' | 'rejected') =>
      request<{ requests: AdminClaimRequest[]; pending: number }>(
        `/api/admin/claim-requests${status ? `?status=${status}` : ''}`,
      ).then((r) => r.requests),

    pendingClaims: () =>
      request<{ requests: AdminClaimRequest[]; pending: number }>(
        '/api/admin/claim-requests?status=pending',
      ).then((r) => r.pending),

    /** `device` registers the IMEI in the same step when it is not known yet. */
    approveClaim: (id: number, device?: ProposedDevice) =>
      request<{ ok: true }>(`/api/admin/claim-requests/${id}/approve`, {
        method: 'POST',
        ...(device ? { body: { device } } : {}),
      }),

    rejectClaim: (id: number, reason?: string) =>
      request<{ ok: true }>(`/api/admin/claim-requests/${id}/reject`, {
        method: 'POST',
        ...(reason ? { body: { reason } } : {}),
      }),

    /** Record every frame from this device for a few minutes. 0 stops it. */
    captureFrames: (id: number, minutes: number) =>
      request<{ captureUntil: string | null }>(`/api/admin/devices/${id}/capture`, {
        method: 'POST',
        body: { minutes },
      }),

    rawLog: (imei?: string, limit = 100) =>
      request<{ entries: Array<Record<string, unknown>> }>(
        `/api/admin/raw-log?limit=${limit}${imei ? `&imei=${encodeURIComponent(imei)}` : ''}`,
      ).then((r) => r.entries),

    /** Detach a device from its owner, e.g. when the vehicle is resold. */
    releaseDevice: (id: number) =>
      request<{ ok: true }>(`/api/admin/devices/${id}/release`, { method: 'POST' }),
  },
};
